import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardBody, CardHeader, Col, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown, Spinner } from 'reactstrap';
import axios from 'axios';
import { useAuth } from '../../../Provider/AuthProvider';

interface Seller {
    rank: number;
    id: number;
    lastname: string;
    firstname: string;
    fullname: string;
    login: string;
    email: string;
    phone: string;
    mobile: string;
    photo: string | null;
    stats: {
        total_orders: number;
        total_customers: number;
        total_amount_ht: number;
        total_vat: number;
        total_amount: number;
        avg_order_ht: number;
        avg_order: number;
    };
    dates: {
        first_order: string;
        last_order: string;
        first_order_timestamp: number | null;
        last_order_timestamp: number | null;
    };
}

const TopSellers = () => {
    const { user } = useAuth();
    const [sellers, setSellers] = useState<Seller[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedPeriod, setSelectedPeriod] = useState("Tous");
    const [totalSellers, setTotalSellers] = useState(0);

    // Récupération des infos utilisateur pour les rôles
    const userinfo = user?.userinfo;
    const isAdmin = userinfo?.admin === "1";
    const isEmployee = userinfo?.admin === "0" && userinfo?.employee === "1";

    // Fonction pour tronquer le texte
    const truncateText = (text: string, maxLength: number) => {
        if (!text) return '';
        return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
    };

    // Fonction pour calculer les dates en fonction de la période sélectionnée
    const getDateRange = (period: string) => {
        const today = new Date();
        let date_start = null;
        let date_end = null;

        switch (period) {
            case "Aujourd'hui":
                date_start = formatDate(today);
                date_end = formatDate(today);
                break;
            case "Hier":
                const yesterday = new Date(today);
                yesterday.setDate(yesterday.getDate() - 1);
                date_start = formatDate(yesterday);
                date_end = formatDate(yesterday);
                break;
            case "7 derniers jours":
                date_end = formatDate(today);
                const last7Days = new Date(today);
                last7Days.setDate(last7Days.getDate() - 7);
                date_start = formatDate(last7Days);
                break;
            case "30 derniers jours":
                date_end = formatDate(today);
                const last30Days = new Date(today);
                last30Days.setDate(last30Days.getDate() - 30);
                date_start = formatDate(last30Days);
                break;
            case "Ce mois":
                date_start = formatDate(new Date(today.getFullYear(), today.getMonth(), 1));
                date_end = formatDate(today);
                break;
            case "Mois dernier":
                const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
                date_start = formatDate(firstDayLastMonth);
                date_end = formatDate(lastDayLastMonth);
                break;
            case "Tous":
            default:
                date_start = null;
                date_end = null;
                break;
        }

        return { date_start, date_end };
    };

    // Fonction pour formater la date en YYYY-MM-DD
    const formatDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const fetchTopSellers = async (period = "Tous") => {
        setLoading(true);
        try {
            // Vérification de l'authentification
            if (!user?.token) {
                console.error('Utilisateur non authentifié - token manquant');
                setLoading(false);
                return;
            }

            if (!user?.entity) {
                console.error('Entité utilisateur manquante');
                setLoading(false);
                return;
            }

            const { date_start, date_end } = getDateRange(period);
            
            // Construire le payload
            const payload: any = {
                action: 'topseller',
                entity: user.entity
            };

            // Ajouter les dates seulement si elles sont définies
            if (date_start) {
                payload.date_start = date_start;
            }
            if (date_end) {
                payload.date_end = date_end;
            }

            console.log('Envoi de la requête avec:', {
                url: 'https://api.apapiecesauto.fr/apiV2/',
                payload,
                token: user.token ? 'Présent' : 'Manquant'
            });

            const response: any = await axios.post(
                'https://api.apapiecesauto.fr/apiV2/',
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            // Vérifier que la réponse est valide - CORRECTION 1
            if (response.data && response.success === true) {
                setSellers(response.data || []);
                setTotalSellers(response.total || 0);
                console.log('Vendeurs récupérés:', response.data);
            } else {
                console.warn('La réponse API ne contient pas success=true', response.data);
                setSellers([]);
                setTotalSellers(0);
            }
        } catch (error: any) {
            console.error('Erreur lors de la récupération des vendeurs:', error);
            
            // Gestion détaillée des erreurs
            if (error.response) {
                console.error('Détails de l\'erreur:', {
                    status: error.response.status,
                    data: error.response.data,
                    headers: error.response.headers
                });

                if (error.response.status === 401) {
                    console.error('Token invalide ou expiré - veuillez vous reconnecter');
                }
            } else if (error.request) {
                console.error('Aucune réponse reçue du serveur:', error.request);
            } else {
                console.error('Erreur de configuration:', error.message);
            }
            
            setSellers([]);
            setTotalSellers(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.token) {
            fetchTopSellers(selectedPeriod);
        } else {
            console.log('En attente de l\'authentification...');
            setLoading(false);
        }
    }, [user?.token, selectedPeriod]);

    const handlePeriodChange = (period: string) => {
        setSelectedPeriod(period);
    };

    const formatCurrency = (amount: number) => {
        return amount?.toFixed(2) || '0.00';
    };

    const formatNumber = (num: number) => {
        return num?.toLocaleString() || '0';
    };

    // Fonction pour obtenir l'initiale du vendeur
    const getInitials = (fullname: string) => {
        return fullname.charAt(0).toUpperCase();
    };

    // Styles inline pour le tableau avec hauteur fixe
    const tableContainerStyle = {
        maxHeight: '400px',
        overflowY: 'auto' as const,
        scrollbarWidth: 'thin' as const,
        msOverflowStyle: 'auto' as const
    };

    const tableHeadStyle = {
        position: 'sticky' as const,
        top: 0,
        zIndex: 1,
        backgroundColor: '#f8f9fa'
    };

    // Vérification si l'utilisateur est authentifié
    if (!user?.token) {
        return (
            <Col xl={6}>
                <Card>
                    <CardBody>
                        <div className="text-center py-4">
                            <i className="ri-error-warning-line display-5 text-warning"></i>
                            <h5 className="mt-3">Authentification requise</h5>
                            <p className="text-muted">
                                Veuillez vous connecter pour voir les top vendeurs
                            </p>
                        </div>
                    </CardBody>
                </Card>
            </Col>
        );
    }

    return (
        <Col xl={6}>
            <Card className="card-height-100">
                <CardHeader className="align-items-center d-flex">
                    <h4 className="card-title mb-0 flex-grow-1">Top Vendeurs</h4>
                    <div className="flex-shrink-0">
                        <UncontrolledDropdown className="card-header-dropdown">
                            <DropdownToggle tag="a" className="text-reset dropdown-btn" role="button">
                                <span className="fw-semibold text-uppercase fs-12">Période: </span>
                                <span className="text-muted">
                                    {selectedPeriod} <i className="mdi mdi-chevron-down ms-1"></i>
                                </span>
                            </DropdownToggle>
                            <DropdownMenu className="dropdown-menu-end">
                                <DropdownItem onClick={() => handlePeriodChange("Tous")}>
                                    Tous
                                </DropdownItem>
                                <DropdownItem onClick={() => handlePeriodChange("Aujourd'hui")}>
                                    Aujourd'hui
                                </DropdownItem>
                                <DropdownItem onClick={() => handlePeriodChange("Hier")}>
                                    Hier
                                </DropdownItem>
                                <DropdownItem onClick={() => handlePeriodChange("7 derniers jours")}>
                                    7 derniers jours
                                </DropdownItem>
                                <DropdownItem onClick={() => handlePeriodChange("30 derniers jours")}>
                                    30 derniers jours
                                </DropdownItem>
                                <DropdownItem onClick={() => handlePeriodChange("Ce mois")}>
                                    Ce mois
                                </DropdownItem>
                                <DropdownItem onClick={() => handlePeriodChange("Mois dernier")}>
                                    Mois dernier
                                </DropdownItem>
                            </DropdownMenu>
                        </UncontrolledDropdown>
                    </div>
                </CardHeader>

                <CardBody>
                    {loading && (
                        <div className="text-center py-4">
                            <Spinner color="primary">
                                Chargement...
                            </Spinner>
                        </div>
                    )}
                    
                    {!loading && sellers.length > 0 && (
                        <div className="table-responsive" style={tableContainerStyle}>
                            <table className="table table-centered table-hover align-middle table-nowrap mb-0">
                                <thead className="text-muted table-light" style={tableHeadStyle}>
                                    <tr>
                                        <th scope="col" >Vendeur</th>
                                        <th scope="col" className="text-center">Commandes</th>
                                        <th scope="col" className="text-center">Clients</th>
                                        {/* Afficher la colonne CA uniquement pour les admins */}
                                        {isAdmin && <th scope="col">CA HT</th>}
                                        <th scope="col">Moyenne</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sellers.map((seller) => (
                                        <tr key={seller.id}>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    <div className="flex-shrink-0 me-2">
                                                        <div className="avatar-sm">
                                                            {seller.photo ? (
                                                                <img 
                                                                    src={seller.photo} 
                                                                    alt={seller.fullname}
                                                                    className="avatar-sm rounded-circle"
                                                                />
                                                            ) : (
                                                                <div className="avatar-title bg-primary-subtle text-primary rounded-circle fs-5">
                                                                    {getInitials(seller.fullname)}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <h5 className="fs-14 my-1 fw-medium">
                                                            <Link 
                                                                to={`/apps-ecommerce-seller-details/${seller.id}`} 
                                                                className="text-reset" 
                                                                title={seller.fullname}
                                                            >
                                                                {truncateText(seller.fullname, 10)}
                                                            </Link>
                                                        </h5>
                                                        <span className="text-muted">
                                                            @{seller.login}
                                                        </span>
                                                        {seller.email && (
                                                            <span className="text-muted d-block small">
                                                                {seller.email}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                <h5 className="fs-14 my-1 fw-normal">{formatNumber(seller.stats.total_orders)}</h5>
                                            </td>
                                            <td className="text-center">
                                                <h5 className="fs-14 my-1 fw-normal">{formatNumber(seller.stats.total_customers)}</h5>
                                            </td>
                                            {/* Afficher les colonnes CA HT et TTC uniquement pour les admins */}
                                            {isAdmin && (
                                                <td>
                                                    <h5 className="fs-14 my-1 fw-normal">{formatCurrency(seller.stats.total_amount_ht)} €</h5>
                                                    <span className="text-muted">HT</span>
                                                    <small className="d-block text-muted">
                                                        TTC: {formatCurrency(seller.stats.total_amount)} €
                                                    </small>
                                                </td>
                                            )}
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <div>
                                                        <h5 className="fs-14 my-1 fw-normal">{formatCurrency(seller.stats.avg_order)} €</h5>
                                                    </div>
                                                    <div className={`badge bg-${seller.rank === 1 ? 'success' : seller.rank === 2 ? 'info' : seller.rank === 3 ? 'warning' : 'secondary'} bg-opacity-10 text-${seller.rank === 1 ? 'success' : seller.rank === 2 ? 'info' : seller.rank === 3 ? 'warning' : 'secondary'}`}>
                                                        #{seller.rank}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    
                    {!loading && sellers.length === 0 && (
                        <div className="text-center py-4">
                            <i className="ri-user-search-line display-5 text-muted"></i>
                            <p className="text-muted mb-0 mt-3">Aucun vendeur trouvé pour la période sélectionnée</p>
                        </div>
                    )}
                    
                    {sellers.length > 0 && (
                        <div className="mt-3 text-center">
                            <small className="text-muted">
                                Affichage des {sellers.length} meilleurs vendeurs
                                {selectedPeriod !== "Tous" && ` pour la période: ${selectedPeriod}`}
                                {sellers.length === 7 }
                            </small>
                        </div>
                    )}
                </CardBody>
            </Card>
        </Col>
    );
};

export default TopSellers;